<template>
  <MModal
    :show="show"
    size="small"
    :aria-label="title"
    :show-close-button="showCloseButton"
    :close-on-escape="closeOnEscape"
    :clickaway="showCloseButton"
    :allow-overflow="allowOverflow"
    @close-modal="$emit('close-modal')"
  >
    <template #header>
      <MHeading size="h4" tag="h2" :bottom-margin="!!$slots.default">
        {{ title }}
      </MHeading>
    </template>

    <div :class="{ 'text-center': textAlign === 'center' }">
      <slot />
    </div>

    <template v-if="$slots.footer || actions.length" #footer>
      <slot name="footer">
        <div
          class="columns is-mobile is-multiline is-vcentered"
          :class="{
            'is-flex-direction-row-reverse': textAlign === 'left',
          }"
        >
          <div
            v-for="(action, i) in actions"
            :key="i"
            class="column"
            :class="{
              'is-12': textAlign === 'center',
              'is-12-mobile is-narrow-large-mobile': textAlign === 'left',
            }"
          >
            <component
              :is="action.component || 'MButton'"
              :block="textAlign === 'left' ? 'mobile' : true"
              v-bind="action.props"
              v-on="action.listeners"
              @click="handleClick(action)"
            >
              {{ action.text }}
            </component>
          </div>
        </div>
      </slot>
    </template>
  </MModal>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'

import MButton from '../../../controls/Button/MButton.vue'
import MLink from '../../../controls/Link/MLink.vue'
import MHeading from '../../../typography/Heading/MHeading.vue'
import MModal from '../MModal.vue'

type ActionsType = {
  text: string
  event?: string
  props?: any
  listeners?: any
  component?: 'MButton' | 'MLink'
}[]

export default defineComponent({
  name: 'MModalDialog',
  components: {
    MHeading,
    MButton,
    MLink,
    MModal,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    /**
     * Whether to show the close button
     */
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    /**
     * Whether to close modal on escape button key press
     */
    closeOnEscape: {
      type: Boolean,
      default: true,
    },
    /**
     * Whether the modal is rendered
     */
    show: {
      type: Boolean,
      default: false,
    },
    /**
     * Text alignment
     *
     * Center: single action form
     * Left: action/destructive form (e.g. OK/Cancel)
     */
    textAlign: {
      type: String,
      default: 'left',
      validator: (prop: string) => ['left', 'center'].includes(prop),
    },
    /**
     * An array of objects, containing component, props, text and listeners,
     * to be passed through to MButton/MLink components
     */
    actions: {
      type: Array as PropType<ActionsType>,
      default: () => [],
    },
    /**
     * Allow elements to overflow the modal
     * This prevents internal scrolling.
     * Ideally only for use in short modals.
     * Useful for MSelectInput dropdowns.
     */
    allowOverflow: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close-modal'],
  methods: {
    handleClick(action: { event: string }) {
      if (action.event) {
        this.$emit('close-modal', action.event)
      }
    },
  },
})
</script>
